import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { AlertCircle, CalendarCheck, Download, Eye, Loader2, RefreshCw, Search } from 'lucide-react';
import styles from './BookingHistory.module.css';
import { useStore } from '../../context/StoreContext';
import useAccess from '../../hooks/useAccess';
import GlobalDateFilter from '../../components/GlobalDateFilter/GlobalDateFilter';
import { formatDate, formatTime, getBillDate, toLocalYMD } from '../../utils/billDates';

const API_ORIGIN = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001')
    .replace(/\/api\/v1(?:\/public)?\/?$/, '')
    .replace(/\/+$/, '');
const INVOICES_API = `${API_ORIGIN}/api/v1/smiify/sm/invoices`;


/** 9876543210 -> 98••••••10 */
const maskPhone = (p) => { const s = String(p || ''); return s.length >= 6 ? `${s.slice(0, 2)}${'•'.repeat(s.length - 4)}${s.slice(-2)}` : '••••'; };

const formatMoney = (value) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value) || 0);

// Last 30 days including today (works across month / year change)
const DEFAULT_RANGE_DAYS = 30;

const getDefaultDateRange = () => {
    const start = new Date();
    start.setDate(start.getDate() - (DEFAULT_RANGE_DAYS - 1));
    return { startDate: toLocalYMD(start), endDate: toLocalYMD(new Date()), label: 'Last 30 Days' };
};

const BookingHistory = () => {
    const navigate = useNavigate();
    const { activeStore } = useStore();
    const storeId = activeStore?._id || activeStore?.id;
    const token = localStorage.getItem('smiify_token');
    const { can, canAny } = useAccess();

    // Live access (the server checks the same rules)
    const canExport = can('billing.export_history');
    const canSeePhone = can('customers.view_phone');
    const canOpen = canAny(['appointments.view_booking_history', 'billing.view_bill']);
    const phoneText = (p) => (canSeePhone ? p : maskPhone(p));

    const [bookings, setBookings] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [dateRange, setDateRange] = useState(getDefaultDateRange);

    /* ------------------------------ Fetch ------------------------------ */
    const fetchBookings = useCallback(async () => {
        if (!token) {
            setIsLoading(false);
            setLoadError('You are logged out. Please log in again.');
            return;
        }
        if (!storeId || storeId === 'global_hq') {
            setIsLoading(false);
            setLoadError('Select a branch to see its bookings.');
            return;
        }

        setIsLoading(true);
        setLoadError('');
        try {
            const response = await fetch(`${INVOICES_API}?status=completed`, {
                headers: { Authorization: `Bearer ${token}`, 'x-store-id': storeId },
            });
            const result = await response.json().catch(() => ({}));

            if (!response.ok || result.success === false) {
                setBookings([]);
                setLoadError(result.message || `Could not load bookings (error ${response.status}).`);
                return;
            }
            setBookings(Array.isArray(result.data) ? result.data : []);
        } catch (error) {
            console.error('Failed to fetch booking history', error);
            setBookings([]);
            setLoadError('Cannot reach the server. Check that the backend is running.');
        } finally {
            setIsLoading(false);
        }
    }, [storeId, token]);

    useEffect(() => {
        fetchBookings();
    }, [fetchBookings]);

    /* ------------------------------ Filters ------------------------------ */
    const filteredBookings = useMemo(() => {
        // Completed bills are counted on the day they were completed
        let result = bookings.map((bk) => ({ ...bk, _ts: getBillDate(bk) }));

        if (dateRange.startDate && dateRange.endDate) {
            result = result.filter((bk) => {
                const day = toLocalYMD(bk._ts);
                return day && day >= dateRange.startDate && day <= dateRange.endDate;
            });
        }

        const query = searchQuery.trim().toLowerCase();
        if (query) {
            result = result.filter((bk) =>
                String(bk.invoiceNo || '').toLowerCase().includes(query) ||
                String(bk.customer?.name || '').toLowerCase().includes(query) ||
                (canSeePhone && String(bk.customer?.phone || '').includes(query))
            );
        }

        return result.sort((a, b) => b._ts - a._ts);
    }, [bookings, searchQuery, dateRange, canSeePhone]);

    const hiddenByDate = bookings.length > 0 && filteredBookings.length === 0 && !searchQuery.trim();

    /* ------------------------------ Export ------------------------------ */
    const handleExportExcel = () => {
        if (filteredBookings.length === 0) return alert('No data to export!');
        const exportData = filteredBookings.map((bk) => ({
            'Bill No': bk.invoiceNo || 'N/A',
            Date: formatDate(bk._ts),
            Time: formatTime(bk._ts),
            'Customer Name': bk.customer?.name || 'Walk-in',
            'Customer Phone': bk.customer?.phone ? phoneText(bk.customer.phone) : 'N/A',
            'Total Services': bk.items?.length || 0,
            'Total Amount': bk.summary?.grandTotal || 0,
            Status: 'COMPLETED',
        }));
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Completed_Bookings');
        XLSX.writeFile(wb, `Bookings_History_${dateRange.startDate}_to_${dateRange.endDate}.xlsx`);
    };

    /* ------------------------------ Render ------------------------------ */
    if (isLoading) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--h-color-neutral-50)' }}>
                <Loader2 className={styles.spin} size={28} color="var(--h-color-primary-600)" />
                <p style={{ marginTop: '12px', fontSize: '11.5px', color: 'var(--h-color-neutral-500)', fontWeight: 700 }}>Loading completed bookings...</p>
            </div>
        );
    }

    return (
        <div className={styles.pageContainer}>
            <div className={styles.toolbar}>
                <div className={styles.toolbarLeft}>
                    <div className={styles.searchBox}>
                        <Search size={15} color="var(--h-color-neutral-400)" />
                        <input
                            type="text"
                            placeholder={canSeePhone ? 'Search name, phone or bill no...' : 'Search name or bill no...'}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--h-color-neutral-500)', marginLeft: '10px' }}>
                        Showing {filteredBookings.length} of {bookings.length} bookings
                    </div>
                </div>

                <div className={styles.toolbarRight}>
                    <GlobalDateFilter
                        startDate={dateRange.startDate}
                        endDate={dateRange.endDate}
                        defaultLabel={dateRange.label}
                        onChange={(range) => setDateRange(range)}
                    />
                    <button className={styles.secondaryBtn} onClick={fetchBookings} title="Reload">
                        <RefreshCw size={14} />
                    </button>
                    {canExport && (
                        <button className={styles.secondaryBtn} onClick={handleExportExcel}>
                            <Download size={14} /> Export Data
                        </button>
                    )}
                </div>
            </div>

            <main className={styles.mainLayout}>
                <div className={styles.tableSection}>
                    <div className={styles.tableWrapper}>
                        <table className={styles.dataTable}>
                            <thead>
                                <tr>
                                    <th>Completed on</th>
                                    <th>Client Details</th>
                                    <th>Total Items</th>
                                    <th style={{ textAlign: 'right' }}>Amount Paid</th>
                                    <th style={{ textAlign: 'center' }}>Status</th>
                                    <th style={{ textAlign: 'right' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loadError ? (
                                    <tr>
                                        <td colSpan="6">
                                            <div className={styles.emptyState}>
                                                <AlertCircle size={36} color="var(--h-color-danger-500)" style={{ marginBottom: '14px' }} />
                                                <h3>Bookings could not be loaded</h3>
                                                <p>{loadError}</p>
                                                <button className={styles.secondaryBtn} style={{ marginTop: '14px' }} onClick={fetchBookings}>
                                                    <RefreshCw size={14} /> Try again
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ) : filteredBookings.length === 0 ? (
                                    <tr>
                                        <td colSpan="6">
                                            <div className={styles.emptyState}>
                                                <CalendarCheck size={36} color="var(--h-color-neutral-300)" style={{ marginBottom: '14px' }} />
                                                <h3>No completed bookings found</h3>
                                                <p>
                                                    {hiddenByDate
                                                        ? `${bookings.length} bookings exist, but none in ${dateRange.label || 'this date range'}. Change the date filter.`
                                                        : 'Adjust your search or date filter, or make sure bills are marked as completed.'}
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredBookings.map((bk) => (
                                        <tr key={bk._id} onClick={() => canOpen && navigate(`/booking/${bk._id}`)} style={canOpen ? undefined : { cursor: 'default' }}>
                                            <td>
                                                <div className={styles.dateBlock}>
                                                    <span className={styles.dateValue}>{formatDate(bk._ts)}</span>
                                                    <span className={styles.timeValue}>{formatTime(bk._ts)}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className={styles.clientCell}>
                                                    <span className={styles.clientName}>{bk.customer?.name || 'Walk-in'}</span>
                                                    <span className={styles.clientPhone}>{bk.customer?.phone ? `+91 ${phoneText(bk.customer.phone)}` : '—'}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <span style={{ fontWeight: 700, color: 'var(--h-color-neutral-700)' }}>
                                                    {bk.items?.length || 0} {bk.items?.length === 1 ? 'Service' : 'Services'}
                                                </span>
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                <span className={styles.moneyCell}>{formatMoney(bk.summary?.grandTotal)}</span>
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                <span className={styles.statusTag}>Completed</span>
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                {canOpen && (
                                                    <button
                                                        className={styles.iconBtn}
                                                        title="Open booking"
                                                        onClick={(e) => { e.stopPropagation(); navigate(`/booking/${bk._id}`); }}
                                                    >
                                                        <Eye size={14} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default BookingHistory;